import { useState, type KeyboardEvent } from "react";
import type { ModifierGroup } from "../../api/types";
import { t } from "../../i18n";
import { ConfirmDialog } from "../../ui/ConfirmDialog";
import { Dialog } from "../../ui/Dialog";
import {
  CHANNEL_CHOICES,
  channelsKey,
  checkGroup,
  deleteGroup,
  groupDraft,
  groupSizes,
  groupUse,
  productsUsing,
  saveGroup,
  type GroupDraft,
  type GroupKind,
  type OptionDraft,
} from "./menu-edit";
import type { MenuEditor } from "./useMenuEditor";

interface Props {
  editor: MenuEditor;
  group: ModifierGroup | null;
  onDone: (message: string) => void;
  onClose: () => void;
}

const noEnter = (event: KeyboardEvent) => {
  if (event.key === "Enter") event.preventDefault();
};

/** Nieuwe keuzegroep / Keuzegroep aanpassen, as in the prototype. */
export function GroupDialog({ editor, group, onDone, onClose }: Props) {
  const { menu, toast } = editor;
  const [draft, setDraft] = useState<GroupDraft>(() => groupDraft(menu, group));
  const [confirmDelete, setConfirmDelete] = useState(false);
  const sizes = groupSizes(menu, group);
  const used = group ? productsUsing(menu, group.id).length : 0;
  const text = t.menuAdmin.groups;
  const set = (change: Partial<GroupDraft>) => {
    setDraft((current) => ({ ...current, ...change }));
  };
  const setOption = (index: number, change: Partial<OptionDraft>) => {
    set({ options: draft.options.map((option, at) => (at === index ? { ...option, ...change } : option)) });
  };

  const chooseKind = (kind: GroupKind) => {
    // as in the prototype: a required choice starts at max 1 without free picks; instructions are free
    if (kind === "choice") set({ kind, maximum: draft.maximum === "0" ? "1" : draft.maximum, free: "0" });
    else if (kind === "instruction") set({ kind, free: "0", bySize: false });
    else set({ kind });
  };

  const save = async () => {
    const problem = checkGroup(draft);
    if (problem) {
      toast(problem, "bad");
      return;
    }
    const { menu: next } = saveGroup(menu, group, draft);
    const title = draft.title.trim();
    const change = group ? `Keuzegroep ${title} aangepast` : `Keuzegroep ${title} aangemaakt`;
    if (await editor.commit(next, change)) {
      onDone(group ? text.saved(title, used) : text.added(title));
      onClose();
    }
  };

  const remove = async () => {
    if (!group) return;
    if (await editor.commit(deleteGroup(menu, group.id), `Keuzegroep ${group.title} verwijderd`)) {
      onDone(text.deleted);
      onClose();
    }
  };

  return (
    <Dialog
      title={group ? text.edit : text.new}
      size="wide"
      onClose={onClose}
      footer={
        <>
          {group && (
            <>
              <button
                type="button"
                className="btn danger"
                disabled={editor.busy}
                onClick={() => {
                  setConfirmDelete(true);
                }}
              >
                {text.remove}
              </button>
              <span className="note">{text.usedFooter(used)}</span>
            </>
          )}
          <span className="spacer" />
          <button type="button" className="btn" onClick={onClose}>
            {t.menuAdmin.cancel}
          </button>
          <button type="button" className="btn primary" disabled={editor.busy} onClick={() => void save()}>
            {t.menuAdmin.product.save}
          </button>
        </>
      }
    >
      <div className="pegrid">
        <label className="field">
          {text.name}
          <input
            value={draft.title}
            placeholder={text.namePlaceholder}
            onKeyDown={noEnter}
            onChange={(event) => {
              set({ title: event.target.value });
            }}
            data-autofocus
          />
        </label>
        <div className="field">
          <span>{text.kind}</span>
          <div className="seg" role="group" aria-label={text.kind}>
            {(["extra", "choice", "instruction"] as const).map((kind) => (
              <button
                key={kind}
                type="button"
                aria-pressed={draft.kind === kind}
                onClick={() => {
                  chooseKind(kind);
                }}
              >
                {text.kinds[kind]}
              </button>
            ))}
          </div>
        </div>
      </div>
      <div className="pegrid">
        {draft.kind === "choice" && (
          <label className="field">
            {text.minimum}
            <input
              type="number"
              min={1}
              value={draft.minimum}
              onKeyDown={noEnter}
              onChange={(event) => {
                set({ minimum: event.target.value });
              }}
            />
          </label>
        )}
        <label className="field">
          <span>
            {text.maximum} <span className="note">{text.maximumHint}</span>
          </span>
          <input
            type="number"
            min={0}
            value={draft.maximum}
            onKeyDown={noEnter}
            onChange={(event) => {
              set({ maximum: event.target.value });
            }}
          />
        </label>
        {draft.kind === "extra" && (
          <label className="field">
            <span>
              {text.free_} <span className="note">{text.freeHint}</span>
            </span>
            <input
              type="number"
              min={0}
              value={draft.free}
              onKeyDown={noEnter}
              onChange={(event) => {
                set({ free: event.target.value });
              }}
            />
          </label>
        )}
      </div>
      <div className="pesh">
        <b>{text.optionsTitle}</b>
        {draft.kind === "instruction" ? (
          <span className="note">{text.instructionsFree}</span>
        ) : (
          <div className="seg" role="group" aria-label={text.optionsTitle}>
            <button
              type="button"
              aria-pressed={!draft.bySize}
              onClick={() => {
                set({ bySize: false });
              }}
            >
              {text.onePrice}
            </button>
            <button
              type="button"
              aria-pressed={draft.bySize}
              onClick={() => {
                // every size starts at the price the option had
                set({
                  bySize: true,
                  options: draft.options.map((option) => ({
                    ...option,
                    prices: Object.fromEntries(sizes.map((size) => [size, option.prices[size] ?? option.price])),
                  })),
                });
              }}
            >
              {text.bySizeLabel(sizes.map((size) => size[0]).join("/"))}
            </button>
          </div>
        )}
      </div>
      <div className="optlist">
        {draft.options.map((option, index) => (
          <div className="optrow" key={index}>
            <input
              className="opt-name"
              value={option.name}
              placeholder={text.optionName}
              aria-label={text.optionNameOf(index + 1)}
              readOnly={option.fixedName}
              title={option.fixedName ? text.fixedName : undefined}
              onKeyDown={noEnter}
              onChange={(event) => {
                setOption(index, { name: event.target.value });
              }}
            />
            {draft.kind !== "instruction" &&
              (draft.bySize ? (
                sizes.map((size) => (
                  <span key={size} className="opt-price">
                    <span className="eur">{size[0]} €</span>
                    <input
                      className="num"
                      inputMode="decimal"
                      value={option.prices[size] ?? option.price}
                      aria-label={text.priceOf(size)}
                      onKeyDown={noEnter}
                      onChange={(event) => {
                        setOption(index, { prices: { ...option.prices, [size]: event.target.value } });
                      }}
                    />
                  </span>
                ))
              ) : (
                <span className="opt-price">
                  <span className="eur">€</span>
                  <input
                    className="num"
                    inputMode="decimal"
                    value={option.price}
                    aria-label={text.price}
                    onKeyDown={noEnter}
                    onChange={(event) => {
                      setOption(index, { price: event.target.value });
                    }}
                  />
                </span>
              ))}
            {/* prototype INSTR_PIZZA: "Gesneden" only at the table, "Niet gesneden" never at the table */}
            <select
              className="opt-channels"
              value={channelsKey(option.channels)}
              aria-label={text.channelsOf(index + 1)}
              title={text.channelsHint}
              onChange={(event) => {
                const choice = CHANNEL_CHOICES.find((channels) => channelsKey(channels) === event.target.value);
                setOption(index, { channels: choice ? [...choice] : null });
              }}
            >
              {CHANNEL_CHOICES.map((channels) => (
                <option key={channelsKey(channels)} value={channelsKey(channels)}>
                  {text.channels[channelsKey(channels)] ?? channelsKey(channels)}
                </option>
              ))}
            </select>
            <button
              type="button"
              className="btn small"
              disabled={index === 0}
              aria-label={text.up}
              onClick={() => {
                const options = draft.options.slice();
                [options[index - 1], options[index]] = [options[index]!, options[index - 1]!];
                set({ options });
              }}
            >
              ↑
            </button>
            <button
              type="button"
              className="btn small"
              aria-label={text.remove}
              onClick={() => {
                set({ options: draft.options.filter((_, at) => at !== index) });
              }}
            >
              ×
            </button>
          </div>
        ))}
        <button
          type="button"
          className="btn small"
          onClick={() => {
            set({
              options: [
                ...draft.options,
                { productId: undefined, name: "", price: "0", prices: {}, fixedName: false, channels: null },
              ],
            });
          }}
        >
          {text.addOption}
        </button>
      </div>
      {group && (
        // where this group is linked, in full (Furkan, 10/10/2026)
        <section className="guselist" aria-label={text.linkedTo}>
          <h3>{text.linkedTo}</h3>
          {groupUse(menu, group.id).map((use) => (
            <p key={use.category}>
              <b>{use.category}</b>
              {": "}
              {use.all
                ? text.wholeLong(use.of)
                : `${use.products.join(", ")} (${text.someOf(use.products.length, use.of)})`}
            </p>
          ))}
          {used === 0 && <p className="note">{text.linkedNone}</p>}
        </section>
      )}
      {confirmDelete && group && (
        <ConfirmDialog
          message={text.confirmDelete(group.title, used)}
          confirm={text.remove}
          onConfirm={() => void remove()}
          onClose={() => {
            setConfirmDelete(false);
          }}
        />
      )}
    </Dialog>
  );
}
